import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  TextField,
  IconButton,
  Tooltip,
  CircularProgress,
  Snackbar,
  Alert,
  MenuItem  
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import { format, parseISO, isValid } from "date-fns";

const AllRTOsData = () => {
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  
  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  });

  const isDateFilterActive = Boolean(year || month);

  const token = localStorage.getItem("token");
  const API_URL = process.env.REACT_APP_API_URL;

  const isSameMonthYear = (date, year, month) => {
    if (!date) return false;
    const d = new Date(date);

    // No date filter → allow all
    if (!year && !month) return true;

    if (year && d.getFullYear().toString() !== year) return false;
    if (
      month &&
      String(d.getMonth() + 1).padStart(2, "0") !== month
    )
      return false;

    return true;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = parseISO(dateString);
    if (!isValid(date)) return "-";
    return format(date, "dd MMM yyyy");
  };
  
  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const date = parseISO(dateString);
    if (!isValid(date)) return "-";
    return format(date, "dd MMM yyyy HH:mm");
  };

  const normalizeDateForSearch = (date) => {
    if (!date) return "";

    const d = new Date(date);
    if (isNaN(d)) return "";

    const day = String(d.getDate()).padStart(2, "0");
    const monthNum = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();

    const monthShort = d.toLocaleString("en-GB", { month: "short" }); // Dec
    const monthLong = d.toLocaleString("en-GB", { month: "long" });  // December

    return [
      `${day}/${monthNum}/${year}`,       // 18/12/2025
      `${day}-${monthNum}-${year}`,       // 18-12-2025
      `${day} ${monthShort} ${year}`,     // 18 Dec 2025
      `${day} ${monthLong} ${year}`,      // 18 December 2025
      `${monthShort} ${day} ${year}`,     // Dec 18 2025
    ]
      .join(" ")
      .toLowerCase();
  };

  const getAllRTOsData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/get-all-rtos-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.success) {

        const flattenRTOData = (data) => {
          const rows = [];

          data.forEach(rto => {
            if (!rto.deducted_skus || rto.deducted_skus.length === 0) {
              rows.push({
                ...rto,
                id: `${rto.id}-0`,
                deducted_sku: "-",
                deducted_qty: "-"
              });
            } else {
              rto.deducted_skus.forEach((skuItem, index) => {
                rows.push({
                  ...rto,
                  id: `${rto.id}-${index}`,
                  
                  sku_code: index === 0 ? rto.sku_code : "",

                  deducted_sku: skuItem.sku,
                  deducted_qty: skuItem.qty
                });
              });
            }
          });

          return rows;
        };

        const flattenedData = flattenRTOData(res.data.data);

        setRows(flattenedData);
        setFilteredRows(flattenedData);
      }

    } catch (err) {
      console.error("Error fetching all rtos data:", err);
      setSnackbar({
        open: true,
        message: "Error fetching all rtos data",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [API_URL, token]);

  useEffect(() => {
    getAllRTOsData();
  }, [getAllRTOsData]);

  // Search filters
  useEffect(() => {
    const q = search.toLowerCase();

    const filtered = rows.filter((r) => {
      // Month + Year filter
      const matchesMonthYear = isSameMonthYear(
        r.created_at,
        year,
        month
      );

      if (!matchesMonthYear) return false;

      // If no search text, month/year match is enough
      if (!q.trim()) return true;

      // Text search
      const createdAtStr = normalizeDateForSearch(r.created_at);

      return (
        r.sku_code?.toLowerCase().includes(q) ||
        r.deducted_skus?.some(s => s.sku.toLowerCase().includes(q)) ||
        r.product_title?.toLowerCase().includes(q) ||
        String(r.awb_id)?.toLowerCase().includes(q) ||
        String(r.order_id)?.toLowerCase().includes(q) ||
        r.created_by?.toLowerCase().includes(q) ||
        createdAtStr.includes(q) ||
        r.item_condition?.toLowerCase().includes(q) ||
        r.ticket_id?.toLowerCase().includes(q)
      );
      });

      setFilteredRows(filtered);
  }, [rows, search, month, year]);

  const columns = [
    // {
    //   field: "sn",
    //   headerName: "S.No",
    //   width: 70,
    //   renderCell: (params) => {
    //     const api = params.api;
    //     const sortedRowIds = api.getSortedRowIds ? api.getSortedRowIds() : [];
    //     if (!sortedRowIds.length) return "-";
    //     const rowIndex = sortedRowIds.indexOf(params.id);
    //     if (rowIndex === -1) return "-";
    //     const page = api.state.pagination.paginationModel.page;
    //     const pageSize = api.state.pagination.paginationModel.pageSize;
    //     if (isNaN(page) || isNaN(pageSize)) return "-";
    //     const totalRows = sortedRowIds.length;
    //     return totalRows - (page * pageSize + rowIndex);
    //   },
    // },
    // {
    //   field: "sn",
    //   headerName: "S.N.",
    //   width: 70,
    //   sortable: false,
    //   renderCell: (params) => {
    //     const api = params.api;
    //     const rowIndex = api.getRowIndexRelativeToVisibleRows(params.id);
    //     return rowIndex !== undefined ? rowIndex + 1 : "-";
    //   },
    // },
    {
      field: "sn",
      headerName: "S.N.",
      width: 70,
      sortable: false,
      renderCell: (params) => {
        const rowIndex = params.api.getRowIndexRelativeToVisibleRows(params.id);

        return (
          paginationModel.page * paginationModel.pageSize +
          rowIndex +
          1
        );
      },
    },
    { field: "marketplaces", headerName: "Marketplace", width: 100 },
    { field: "pickup_partner", headerName: "Pickup Partner", width: 120 },
    { field: "awb_id", headerName: "AWB Id", width: 150 },        
    { 
      field: "return_date", 
      headerName: "Return Date", 
      width: 100,
      renderCell: (params) => formatDate(params.value)
    },
    { field: "sku_code", headerName: "Ordered SKU", width: 200 },
    // { field: "deducted_skus", headerName: "Deducted SKUs", width: 200, renderCell: (params) => {
    //   const skus = params.value || [];
    //   if (skus.length === 0) return "-";
    //   return skus.map(s => `${s.sku}`).join(", ");
    // }},
    { field: "deducted_sku", headerName: "Deducted SKU", width: 200 },
    { field: "return_qty", headerName: "Return Qty", width: 100},    
    { field: "product_title", headerName: "Title", width: 280 },
    { field: "order_id", headerName: "order_id", width: 180 },
    { 
      field: "order_date", 
      headerName: "Dispatched Date",
      width: 150,
      renderCell: (params) => formatDate(params.value)
    },        
    { field: "item_condition", headerName: "Item Condition", width: 120 },
    { field: "claim_raised", headerName: "Claim Raised", width: 110},    
    { field: "ticket_id", headerName: "Ticket Id", width: 150 },
    { field: "comments", headerName: "Comments", width: 120 },
    { field: "type_of_return", headerName: "Return Type", width: 200 },
    { 
      field: "created_at", 
      headerName: "Created At", 
      width: 150,
      renderCell: (params) => formatDateTime(params.value) 
    },
    { field: "created_by", headerName: "Created By", minWidth: 220 },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
        {/* Header */}
        <Box
          sx={{
            mb: 2,
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography variant="h5" fontWeight={600}>
            All RTOs ({filteredRows.length})
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {isDateFilterActive && (
              <Tooltip title="Clear date filter">
                <IconButton
                  onClick={() => {
                    setYear("");
                    setMonth("");
                  }}
                  sx={{
                    width: 35,
                    height: 35,
                    backgroundColor: "#c7001eff",
                    color: "#fff",
                    "&:hover": { backgroundColor: "#903341ff", color: "#fff" },
                  }}
                >
                  ✕
                </IconButton>
              </Tooltip>
            )}

            <TextField
              select
              size="small"
              label="Month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              sx={{
                minWidth: 100,
                backgroundColor: month ? "#e3f2fd" : "inherit",
              }}
            >
              <MenuItem value="">All</MenuItem>
              {Array.from({ length: 12 }, (_, i) => (
                <MenuItem
                  key={i}
                  value={String(i + 1).padStart(2, "0")}
                >
                  {String(i + 1).padStart(2, "0")}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="Year"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              sx={{
                minWidth: 100,
                backgroundColor: year ? "#e3f2fd" : "inherit",
              }}
            >
              <MenuItem value="">All</MenuItem>
              {["2024", "2025", "2026"].map((yr) => (
                <MenuItem key={yr} value={yr}>
                  {yr}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              size="small"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <SearchIcon color="action" sx={{ mr: 1 }} />
                ),
              }}
            />

            <Tooltip title="Refresh">
              <IconButton onClick={getAllRTOsData} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Table */}
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 300,
            }}
          >
            <CircularProgress />
          </Box>
        ) : filteredRows.length === 0 ? (
          <Typography sx={{ textAlign: "center", py: 5 }}>
            No RTOs data found.
          </Typography>
        ) : (
          <div style={{ height: 600, width: "100%" }}>
            <DataGrid
              rows={filteredRows}
              columns={columns}
              initialState={{
                sorting: {
                  sortModel: [{ field: "created_at", sort: "desc" }],
                },
              }}
              getRowId={(row) => row.id}
              pagination
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[10, 20, 50]}
              disableSelectionOnClick
            />
          </div>
        )}

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() =>
            setSnackbar({ ...snackbar, open: false })
          }
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Paper>
    </Box>
  );
};

export default AllRTOsData;
